/**
 * Integrations catalogue — the data the /integrationen funnel is composed from.
 *
 * CANONICAL PATTERN (2026-08-17, supersedes the note this replaced): a platform gets a
 * `systems` entry here so the funnel can offer it, and that is all it gets by default —
 * there is no dynamic `/integrationen/:slug` page and none is planned. A platform earns a
 * STATIC detail page on the shared `components/common/PlatformIntegrationPage` shell
 * (today: Amazon SP-API, Mirakl, WooCommerce, Shopware), built ONE AT A TIME and only
 * behind the evidence trigger: conversion tracking live AND a third system showing
 * ≥5 clicks in 14 days or ≥1 attributed conversion. Adding an entry here is cheap;
 * adding a page is a page nobody may be searching for, so it waits for the number.
 * The earlier plan — one dynamic page over 16 slugs — is archived at
 * `.claude/briefs/archive/denqfabrik-integration-pages.md` and is not the pattern.
 *
 * The seven flows are not invented for marketing: they are the canonical Script Butler
 * process profiles (inventory / prices / orders / tracking / returns-refunds /
 * supplier-order / goods-receipt), so the site and the product use one vocabulary.
 *
 * `flows` per system is a CLAIM. Only list what we would actually take on — step 3 of
 * the funnel offers the intersection, so an over-generous entry promises work we would
 * then have to decline.
 *
 * Everything the visitor READS is language-dependent and therefore injected: each
 * export below is a factory over the `funnel` translation slice (2026-08-13, the funnel
 * was hardcoded German on the /en routes too). Ids, slugs, roles and system NAMES stay
 * here — they are proper nouns and language-invariant.
 */
import type {
  FunnelAnswers,
  FunnelCatalogue,
  FunnelConfig,
  FunnelContactConfig,
  FunnelLabels,
  FunnelOutcome,
  FunnelSummary,
  FunnelSystem,
} from '@denqfabrik/landing-ui';
import type { FunnelFlowId, FunnelScaleId, FunnelTranslation } from '../i18n/translations';

export const ZOOM_URL = 'https://scheduler.zoom.us/denqfabrik/first-meeting';

/** Fills `{slot}` placeholders. The whole i18n interpolation we need — no library. */
const fill = (template: string, slots: Record<string, string | number>): string =>
  Object.entries(slots).reduce((s, [k, v]) => s.replace(`{${k}}`, String(v)), template);

/** Display order of the flows. The copy per id lives in `funnel.flows`. */
const FLOW_ORDER: FunnelFlowId[] = [
  'stock',
  'price',
  'orders',
  'tracking',
  'returns',
  'supplier',
  'receipt',
  'catalog',
  'invoices',
  'tickets',
];

const SCALE_ORDER: FunnelScaleId[] = ['daily', 'hourly', 'fast', 'unknown'];

const systems: FunnelSystem[] = [
  // ── Shop ────────────────────────────────────────────────────────────────
  {
    slug: 'shopify',
    name: 'Shopify',
    role: 'shop',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'shopware',
    name: 'Shopware',
    role: 'shop',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'woocommerce',
    name: 'WooCommerce',
    role: 'shop',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  {
    slug: 'magento',
    name: 'Magento',
    role: 'shop',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  // ── ERP / Warenwirtschaft ───────────────────────────────────────────────
  {
    slug: 'plentymarkets',
    name: 'Plentymarkets',
    role: 'erp',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns', 'supplier', 'receipt'],
  },
  {
    slug: 'afterbuy',
    name: 'Afterbuy',
    role: 'erp',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'jtl',
    name: 'JTL',
    role: 'erp',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns', 'receipt'],
  },
  {
    slug: 'weclapp',
    name: 'weclapp',
    role: 'erp',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  {
    slug: 'aeins',
    name: 'A.eins',
    role: 'erp',
    flows: ['stock', 'orders', 'supplier', 'receipt'],
  },
  // ── Marktplätze ─────────────────────────────────────────────────────────
  {
    slug: 'amazon',
    name: 'Amazon',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'kaufland',
    name: 'Kaufland',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'otto',
    name: 'OTTO',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'ebay',
    name: 'eBay',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'mirakl',
    name: 'Mirakl (Conrad, OBI, XXXLutz …)',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking', 'returns'],
  },
  {
    slug: 'tiktok-shop',
    name: 'TikTok Shop',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  {
    slug: 'temu',
    name: 'Temu',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  {
    slug: 'manomano',
    name: 'ManoMano',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  {
    slug: 'cdiscount',
    name: 'Cdiscount',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  {
    slug: 'zalando',
    name: 'Zalando',
    role: 'marketplace',
    flows: ['stock', 'price', 'orders', 'tracking'],
  },
  // ── Portale / Pools ─────────────────────────────────────────────────────
  { slug: 'idealo', name: 'Idealo', role: 'portal', flows: ['price'] },
  { slug: 'itscope', name: 'itscope', role: 'pool', flows: ['price', 'stock'] },
];

function buildCatalogue(f: FunnelTranslation): FunnelCatalogue {
  return {
    flows: FLOW_ORDER.map((id) => ({ id, ...f.flows[id] })),
    systems,
  };
}

export function buildFunnelConfig(f: FunnelTranslation): FunnelConfig {
  return {
    catalogue: buildCatalogue(f),
    sourceRoles: ['shop', 'erp'],
    targetRoles: ['marketplace', 'portal', 'pool'],
    // Cadence, not order volume (René, 2026-08-12). A seller launching on a new
    // marketplace cannot answer "how many orders", and it does not drive our cost
    // anyway — runs x work-per-run does. A question whose answer changes nothing
    // costs a click and buys nothing.
    scaleOptions: SCALE_ORDER.map((id) => ({ id, label: f.scaleOptions[id] })),
  };
}

export function buildFunnelLabels(f: FunnelTranslation): FunnelLabels {
  const l = f.labels;
  return {
    source: l.source,
    target: l.target,
    flows: l.flows,
    scale: l.scale,
    back: l.back,
    continueLabel: l.continueLabel,
    stepIndicator: (current, total) => fill(l.stepIndicator, { current, total }),
    result: l.result,
    close: l.close,
    nonStandardBadge: l.nonStandardBadge,
    nonStandardHint: l.nonStandardHint,
  };
}

/**
 * Denqfabrik's outcome: how we work, not a quote — it is derived from four clicks, and
 * pretending otherwise would be caught at the first real conversation. It carries no
 * price (see the note at the return); the one public price line is
 * `pricingPage.tiers` on /de/pricing (PRICING-STRATEGY.md §0.0, update 2026-09-21).
 */
export function buildResolveOutcome(
  f: FunnelTranslation,
): (answers: FunnelAnswers, summary: FunnelSummary) => FunnelOutcome {
  const o = f.outcome;

  return (answers, summary) => {
    const flowCount = summary.flows.length + (answers.flowCustom?.trim() ? 1 : 0);
    const channels = summary.targets.length + (answers.targetCustom?.trim() ? 1 : 0);

    const details = [
      // Counted inline rather than swapped as a whole sentence: German inflects
      // ("den Ablauf" / "die 3 Abläufe", "einen Kanal" / "3 Kanäle") where English
      // does not, so each language owns both number forms AND the frame around them.
      fill(o.build, {
        flows: flowCount === 1 ? o.flowsOne : fill(o.flowsMany, { count: flowCount }),
        channels: channels === 1 ? o.channelsOne : fill(o.channelsMany, { count: channels }),
      }),
      // The safety promise, and the thing no €24/month tool can offer. True by
      // construction: there is no test system, every counterparty is production —
      // which is exactly why a human walks the first one through.
      o.manualTest,
      o.monitored,
      o.directDeveloper,
    ];

    if (answers.scaleId === 'fast') {
      // Guardrail, not a sales line: a 5-minute cadence is only safe when the job reads
      // a delta. An O(total-state) pull at that rhythm is the k4hrl worker-starvation
      // incident, so the tighter tempo is confirmed after we have seen the systems.
      details.push(o.fastCadence);
    }
    if (summary.nonStandardFlows.length > 0) {
      details.push(fill(o.nonStandard, { flows: summary.nonStandardFlows.join(', ') }));
    }

    // Deliberately NO price (René, 2026-08-12). It gets set after looking the company
    // up, because size determines both what they will pay and what they need to believe
    // — a number guessed from four clicks argues against that. The public price line
    // lives on /de/pricing.
    // "Machbar" and "Ihr Setup" were both wrong (René, 2026-08-12): of course it is
    // makeable, and the customer neither knows nor cares what the setup is. What they
    // are deciding is whether this project will succeed — so the payoff is HOW WE WORK.
    return { headline: o.headline, subline: o.subline, details };
  };
}

/**
 * Plain-text briefing composed from the clicks — nobody has to type it.
 *
 * Follows the VISITOR's language, not ours, so the mail we receive says what they
 * actually had on screen.
 */
export function composeBriefing(summary: FunnelSummary, f: FunnelTranslation): string {
  const b = f.briefing;
  return [
    summary.source ? `${b.system}: ${summary.source}` : null,
    summary.targets.length ? `${b.channels}: ${summary.targets.join(', ')}` : null,
    summary.flows.length ? `${b.flows}: ${summary.flows.join(', ')}` : null,
    summary.nonStandardFlows.length
      ? `${b.nonStandard}: ${summary.nonStandardFlows.join(', ')}`
      : null,
    summary.scale ? `${b.cadence}: ${summary.scale}` : null,
    summary.custom.length ? `\n${b.notes}:\n${summary.custom.join('\n')}` : null,
  ]
    .filter((l): l is string => l !== null)
    .join('\n');
}

/**
 * Sends through the existing `/api/contact` route — Resend server-side, already
 * behind `contactSpamGuard()` (server.js:51). Deliberately reuses that endpoint
 * instead of adding a second mail path.
 *
 * Replaces the old `mailto:` button (René, 2026-08-12: "who knows whether the mail
 * app will open"). A mailto depends on a configured desktop mail client; roughly
 * nobody on a work laptop has one, and the failure is silent.
 */
/**
 * Identifies a configurator lead, in the `/api/contact` payload AND in the GA4
 * `lead_type` dimension. Exported so the analytics side imports the same constant
 * rather than re-typing the string — the two are one value, and a silent drift
 * between them would split the segment while every test stayed green.
 */
export const FUNNEL_LEAD_TYPE = 'Konfigurator';

export function buildFunnelContact(f: FunnelTranslation): FunnelContactConfig {
  const c = f.contact;
  return {
    title: c.title,
    hint: c.hint,
    fields: [
      { name: 'name', label: c.nameLabel, required: true, autoComplete: 'name' },
      {
        name: 'email',
        label: c.emailLabel,
        type: 'email',
        required: true,
        autoComplete: 'email',
      },
      { name: 'company', label: c.companyLabel, required: true, autoComplete: 'organization' },
    ],
    submitLabel: c.submitLabel,
    submittingLabel: c.submittingLabel,
    successTitle: c.successTitle,
    successBody: c.successBody,
    errorBody: c.errorBody,
    // Matches SPAM_GUARD_FIELD in @denqfabrik/landing-ui. Never rename: the server
    // half checks exactly this name, and a mismatch silently disables the guard.
    honeypotField: 'website',
    onSubmit: async (values, summary) => {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: values.name,
          email: values.email,
          company: values.company,
          systems: [summary.source, ...summary.targets].filter(Boolean).join(' → '),
          selectedOption: FUNNEL_LEAD_TYPE,
          message: composeBriefing(summary, f),
          website: values.website ?? '',
          elapsedMs: values.elapsedMs,
        }),
      });
      if (!res.ok) throw new Error(`contact endpoint returned ${res.status}`);
    },
  };
}
